import React from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js"
import { Bar } from "react-chartjs-2"

interface BarChartProps {
  data: {
    대전Data: any
    보령Data: any
    여수Data: any
    정읍Data: any
  }
  height?: string
  width?: string
}

// Chart.js 플러그인 등록
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

const Page3Barchart4: React.FC<BarChartProps> = ({
  data: { 정읍Data, 대전Data, 보령Data, 여수Data },
  height = "300px",
  width = "500px",
}) => {
  const labels = ["정읍", "대전", "보령", "여수"]

  // 값 계산 함수
  const calculateValue = (data: any, type: string) => data?.[type] || 0

  const calculateIfGridBy = (data: any, type: string): number => {
    if (!data) return 0 // 데이터가 없는 경우 기본값 반환

    const value = data?.[type] || 0 // 요청된 type의 값 추출
    const totalBuyCost = data?.totalBuyCost || 0 // totalBuyCost 값 추출
    const totalSellCost = data?.totalSellCost

    // totalBuyCost가 0보다 크면 value를 음수로 반환
    if (totalBuyCost > 0) {
      return -value
    } else if (totalSellCost > 0) {
      return data?.excessGen * data?.smp
    }

    return value // 기본값 반환
  }

  const calculateBenefit = (data: any, field: string): number => {
    if (!data) return 0 // 데이터가 없는 경우 0 반환

    const { excessGen, totalSellCost, smp, benefit } = data // 필요한 값 추출

    if (field === "benefit") {
      if (excessGen > 0) {
        // 발전량 초과
        return totalSellCost - excessGen * smp // 숫자 반환
      } else {
        // 발전량 부족
        return benefit || 0 // 숫자 반환
      }
    }

    return 0 // 기본값
  }

  const chartData = {
    labels,
    datasets: [
      {
        label: "거래전 판매/구매 금액",
        data: [
          calculateIfGridBy(정읍Data, "ifGridBuy"),
          calculateIfGridBy(대전Data, "ifGridBuy"),
          calculateIfGridBy(보령Data, "ifGridBuy"),
          calculateIfGridBy(여수Data, "ifGridBuy"),
        ],
        backgroundColor: "#FCB251",
        borderRadius: 5,
      },
      {
        label: "거래후 판매금액",
        data: [
          calculateValue(정읍Data, "totalSellCost"),
          calculateValue(대전Data, "totalSellCost"),
          calculateValue(보령Data, "totalSellCost"),
          calculateValue(여수Data, "totalSellCost"),
        ],
        backgroundColor: "#4282EF",
        borderRadius: 5,
      },
      {
        label: "거래후 구매금액",
        data: [
          -calculateValue(정읍Data, "totalBuyCost"), // 음수 값으로 변환
          -calculateValue(대전Data, "totalBuyCost"),
          -calculateValue(보령Data, "totalBuyCost"),
          -calculateValue(여수Data, "totalBuyCost"),
        ],
        backgroundColor: "#B89EFA",
        borderRadius: 5,
      },
      {
        label: "마을간 거래효과",
        data: [
          calculateBenefit(정읍Data, "benefit"),
          calculateBenefit(대전Data, "benefit"),
          calculateBenefit(보령Data, "benefit"),
          calculateBenefit(여수Data, "benefit"),
        ],
        backgroundColor: "#94D474",
        borderRadius: 5,
      },
    ],
  }

  const options: import("chart.js").ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            const formattedValue = (Number(value) / 10000).toFixed(0) // 소수점 없는 문자열로 변환
            return `${Number(formattedValue).toLocaleString()} 만원` // 숫자로 변환 후 로컬 포맷 적용
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (tooltipItem: any) {
            const label = tooltipItem.dataset.label || "" // 데이터셋의 레이블 가져오기
            const value = Math.abs(Number(tooltipItem.raw)) // 값의 절대값을 숫자로 변환
            const formattedValue = (value / 10000).toFixed(0) // 소수점 없는 문자열로 변환
            return `${label}: ${Number(formattedValue).toLocaleString()} 만원` // 숫자로 변환 후 로컬 포맷 적용
          },
        },
      },
    },
  }

  return (
    <div style={{ height, width }}>
      <Bar data={chartData} options={options} />
    </div>
  )
}

export default Page3Barchart4
